import React, { FC, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { icons } from "../../../icons";
import { HeadLine3 } from "../../typography/styled";
import Button from "../../button";
import Input from "../../input";
import Select from "../../select";
import Toggle from "../../toggle";
import { clusterSettingsService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
} from "../create-node-pool/styles";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (data?: any) => void;
  dataToEdit?: any;
};

const CreateAddonRelease: FC<ModalProps> = ({ closeModal, closeModalCancel, dataToEdit }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [fetchingRelease, setFetchingRelease] = useState(false);
  const [releaseData, setReleaseData] = useState<any>(null);
  const [stable, setStable] = useState(dataToEdit?.stable ?? false);
  const [forWorker, setForWorker] = useState(dataToEdit?.for_worker ?? false);
  const [addons, setAddons] = useState<any[]>([]);
  const [kubeVersions, setKubeVersions] = useState<any[]>([]);
  const [selectedKubeVersions, setSelectedKubeVersions] = useState<any[]>([]);
  const isEdit = !!dataToEdit?.id;
  const hideAddonField = !!dataToEdit?._hideAddonField;

  // Fetch addons list (for addon selector)
  useEffect(() => {
    if (hideAddonField) return;
    clusterSettingsService
      .getAddons({ page_size: 200 })
      .then((res) => {
        const list = (res.data?.objects || []) as any[];
        setAddons(list.map((a) => ({ value: a.id.toString(), label: a.name })));
      })
      .catch(() => {});
  }, [hideAddonField]);

  // Fetch kubernetes versions list
  useEffect(() => {
    clusterSettingsService
      .getVersions({ page_size: 200 })
      .then((res) => {
        const list = (res.data?.objects || []) as any[];
        setKubeVersions(list.map((v) => ({ value: v.id, label: v.id, id: v.id })));
      })
      .catch(() => {});
  }, []);

  // Fetch release detail by ID when editing
  useEffect(() => {
    if (!isEdit) return;
    setFetchingRelease(true);
    clusterSettingsService
      .getAddonRelease(dataToEdit.id)
      .then((res) => {
        const data = res.data;
        setReleaseData(data);
        setStable(data.stable ?? false);
        setForWorker(data.for_worker ?? false);
        // Set selected kube versions from fetched data
        if (data.kube_versions?.length) {
          setSelectedKubeVersions(
            data.kube_versions.map((v: string) => ({ value: v, label: v, id: v }))
          );
        }
      })
      .catch((err) => {
        viewAlert({
          severity: "error",
          message: err?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      })
      .finally(() => setFetchingRelease(false));
  }, [isEdit, dataToEdit?.id]);

  const handleKubeVersionChange = useCallback((item: any) => {
    setSelectedKubeVersions((prev) => {
      const exists = prev.find((v) => v.value === item.value);
      if (exists) {
        return prev.filter((v) => v.value !== item.value);
      }
      return [...prev, item];
    });
  }, []);

  const source = isEdit && releaseData ? releaseData : dataToEdit;

  const onSubmit = (values: any) => {
    setLoading(true);
    let valueTemplate = null;
    if (values.value_template) {
      try {
        valueTemplate = JSON.parse(values.value_template);
      } catch {
        viewAlert({ severity: "error", message: t("INVALID_JSON") });
        setLoading(false);
        return;
      }
    }
    const data = {
      addon_id: +values.addon_id,
      repo_url: values.repo_url,
      chart_name: values.chart_name,
      chart_namespace: values.chart_namespace,
      version: values.version,
      stable,
      for_worker: forWorker,
      value_template: valueTemplate,
      kube_versions: selectedKubeVersions.map((v) => v.value),
    };
    const action = isEdit
      ? clusterSettingsService.updateAddonRelease(dataToEdit.id, data)
      : clusterSettingsService.createAddonRelease(data);

    action
      .then(() => {
        setLoadingComplete(true);
        setTimeout(() => {
          closeModal({ success: true });
          setLoading(false);
          setLoadingComplete(false);
        }, 1500);
      })
      .catch((err) => {
        setLoading(false);
        viewAlert({
          severity: "error",
          message: err?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      });
  };

  const { values, errors, touched, handleSubmit, handleChange, setFieldValue } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      addon_id: yup.string().required(t("REQUIRED_FIELD")),
      version: yup.string().required(t("REQUIRED_FIELD")),
      repo_url: yup.string().required(t("REQUIRED_FIELD")),
      chart_name: yup.string().required(t("REQUIRED_FIELD")),
      chart_namespace: yup.string().required(t("REQUIRED_FIELD")),
    }),
    initialValues: {
      addon_id: source?.addon_id?.toString() || "",
      repo_url: source?.repo_url || "",
      chart_name: source?.chart_name || "",
      chart_namespace: source?.chart_namespace || "",
      version: source?.version || "",
      value_template: source?.value_template ? JSON.stringify(source.value_template, null, 2) : "",
    },
    onSubmit,
  });

  return (
    <Container style={{ width: "520px" }}>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: "10px", marginTop: "10px" }}>
          {isEdit ? t("EDIT_ADDON_RELEASE") : t("CREATE_ADDON_RELEASE")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        {fetchingRelease ? (
          <div style={{ padding: "20px", textAlign: "center" }}>{t("LOADING")}...</div>
        ) : (
          <>
            {!hideAddonField && (
              <Select title={t("ADDON")} data={addons} selectedValue={values.addon_id} onChange={(val: any) => setFieldValue("addon_id", val?.toString())} fieldName="addon_id" errors={errors} touched={touched} hasSearch required />
            )}
            <Input title={t("REPO_URL")} fieldName="repo_url" value={values.repo_url} handleChange={handleChange} errors={errors} touched={touched} required />
            <Input title={t("CHART_NAME")} fieldName="chart_name" value={values.chart_name} handleChange={handleChange} errors={errors} touched={touched} required />
            <Input title={t("CHART_NAMESPACE")} fieldName="chart_namespace" value={values.chart_namespace} handleChange={handleChange} errors={errors} touched={touched} required />
            <Input title={t("VERSION")} fieldName="version" value={values.version} handleChange={handleChange} errors={errors} touched={touched} required />
            <Toggle title={t("STABLE")} value={stable} onChange={() => setStable(!stable)} />
            <Toggle title={t("FOR_WORKER")} value={forWorker} onChange={() => setForWorker(!forWorker)} />
            <Input title={t("VALUE_TEMPLATE")} fieldName="value_template" value={values.value_template} handleChange={handleChange} errors={errors} touched={touched} isTextArea />
            <Select
              title={t("KUBE_VERSIONS")}
              data={kubeVersions}
              selectedValue={selectedKubeVersions}
              onChange={() => {}}
              multi
              multipleOnChange={handleKubeVersionChange}
              hasSearch
            />
            <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
              <Button variant="stroke" size="display" customStyles={{ width: "177px", justifyContent: "center" }} onClick={closeModalCancel} title={t("CANCEL")} />
              <Button variant="primary" size="display" loading={loading} loadingComplete={loadingComplete} customStyles={{ width: "177px", justifyContent: "center" }} onClick={handleSubmit} title={isEdit ? t("SAVE") : t("CREATE")} />
            </div>
          </>
        )}
      </BodyModal>
    </Container>
  );
};

export default CreateAddonRelease;
